/**
 * Host Me — VAT price breakdown for MANUALLY KEYED bookings.
 *
 * Ops agree a direct booking one of two ways, and the form now accepts both:
 *
 *   'inclusive_total'   — "3,000 for the stay, VAT included"  ← how deals are actually struck
 *   'nightly_exclusive' — "750 a night, before VAT"           ← the original behaviour
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE ONE RULE — never round-trip a total through a nightly figure
 * ─────────────────────────────────────────────────────────────────────────────
 * The agreed total is the commercial truth. Deriving a nightly rate from it,
 * rounding that to 2dp and multiplying back lands on 2,999.99 for the example
 * above — a figure the guest never agreed to, on a document we invoice from.
 * So in `inclusive_total` mode the typed amount is carried through UNTOUCHED and
 * the nights count never enters the money at all; it is used only for the
 * informational average shown to the operator.
 *
 * VAT is taken as the DIFFERENCE (`total − rounded subtotal`) rather than computed
 * independently, so the three figures reconcile at 2dp — the cross-check
 * `be/services/capturedVat.js` (`fromDirect`) relies on, since a direct booking's
 * `totalAmount` is written as `subtotal + tax`. (Reconcile, not float-equal: 0.09 + 0.01
 * is 0.0999…9 in IEEE754. `billingBuilder` compares within a tolerance for that reason.)
 *
 * ⚠️ NO RATE CONSTANT LIVES HERE, deliberately. `web/src/config/moneyModel.js`
 * carries the money model's vocabulary and explicitly refuses to carry its rates,
 * to stop a second copy of the model drifting from `be/config/pricingEconomics.js`.
 * The VAT percentage is a form input and is passed in by the caller.
 */

/** Money is stored and displayed to 2dp; every figure leaving here is rounded once. */
const round2 = (n) => Math.round(n * 100) / 100;

/** The two ways ops can state a price. Mirrors nothing on the backend — UI only. */
export const PRICE_ENTRY_MODE = {
  INCLUSIVE_TOTAL: 'inclusive_total',
  NIGHTLY_EXCLUSIVE: 'nightly_exclusive',
  EXCLUSIVE_SUBTOTAL: 'exclusive_subtotal'
};

const EMPTY = { subtotal: 0, tax: 0, total: 0 };

const toNumber = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Agreed VAT-inclusive stay total → the `rate` breakdown.
 * The total is authoritative: it is returned exactly as typed.
 */
export const breakdownFromInclusiveTotal = (stayTotal, vatPercentage) => {
  const total = round2(toNumber(stayTotal));
  if (!total) return { ...EMPTY };

  const rate = toNumber(vatPercentage) / 100;
  const subtotal = round2(total / (1 + rate));
  return { subtotal, tax: round2(total - subtotal), total };
};

/**
 * A VAT-EXCLUSIVE stay subtotal → the `rate` breakdown. VAT is added on top.
 * This is the edit form's basis, and what the nightly path reduces to.
 */
export const breakdownFromExclusiveSubtotal = (staySubtotal, vatPercentage) => {
  const subtotal = round2(toNumber(staySubtotal));
  if (!subtotal) return { ...EMPTY };

  const tax = round2(subtotal * (toNumber(vatPercentage) / 100));
  return { subtotal, tax, total: round2(subtotal + tax) };
};

/**
 * VAT-exclusive nightly rate × nights → the `rate` breakdown.
 * Unchanged from the original create-form behaviour.
 */
export const breakdownFromNightlyRate = (pricePerNight, nights, vatPercentage) =>
  breakdownFromExclusiveSubtotal(toNumber(pricePerNight) * toNumber(nights), vatPercentage);

/**
 * Single entry point for the booking forms — resolves whichever mode is active.
 * @param {Object} p
 * @param {String} p.mode           a PRICE_ENTRY_MODE value
 * @param {Number|String} p.amount  the figure the operator typed (total, or nightly rate)
 * @param {Number} p.nights         used ONLY by the nightly mode
 * @param {Number|String} p.vatPercentage
 */
export const resolvePriceBreakdown = ({ mode, amount, nights, vatPercentage }) => {
  if (mode === PRICE_ENTRY_MODE.NIGHTLY_EXCLUSIVE) {
    return breakdownFromNightlyRate(amount, nights, vatPercentage);
  }
  if (mode === PRICE_ENTRY_MODE.EXCLUSIVE_SUBTOTAL) {
    return breakdownFromExclusiveSubtotal(amount, vatPercentage);
  }
  return breakdownFromInclusiveTotal(amount, vatPercentage);
};

/**
 * The VAT rate a STORED breakdown was billed at, as a whole percent, for labelling a
 * saved booking. Hardcoding "(15%)" on a display row is wrong for the 0%-rated ones and
 * for anything saved under an older rate — and a booking's label should come from the
 * booking, not from today's default.
 *
 * Returns a ready-to-append suffix — ` (15%)` — or an empty string when the breakdown
 * cannot say, so a caller appends it to its own Arabic label and this file stays free of
 * presentation strings.
 */
export const vatPercentSuffix = (subtotal, tax) => {
  const base = toNumber(subtotal);
  if (!base) return '';
  const t = typeof tax === 'number' ? tax : parseFloat(tax);
  if (!Number.isFinite(t)) return '';
  return ` (${Math.round((t / base) * 100)}%)`;
};

/**
 * The average night, for DISPLAY ONLY — shown so the operator can sanity-check that
 * the system read the deal the way they meant it.
 *
 * ⚠️ Never feed this back into a price, a report or a rate push. It is an average of a
 * negotiated stay total, not a rate anybody agreed to per night.
 */
export const averagePerNight = (amount, nights) => {
  const n = toNumber(nights);
  return n ? round2(toNumber(amount) / n) : 0;
};
